import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ATTENDANCEALL } from '../../services/actions';
import { Box, Typography, Container, Paper, Grid, Card, Stack, Chip } from '@mui/material';
import { BsClockFill, BsCalendarCheck, BsHourglassSplit, BsCheckCircleFill, BsExclamationTriangleFill } from 'react-icons/bs';
import XTable from '../extra/XTableAnother';
import ActionBar from '../extra/ActionBar';
import { FormHeroHeader } from '../extra/FormLayout';

export default function MyAttendance() {
    const dispatch = useDispatch();
    const attendanceAllState = useSelector(state => state.attendanceallState);
    const auth = useSelector(state => state.loginState?.auth);
    const logs = Array.isArray(attendanceAllState?.data) ? attendanceAllState.data : (attendanceAllState?.data?.results || []);

    useEffect(() => {
        dispatch({ type: `${ATTENDANCEALL}_GET`, payload: "attendance/logs/?mine=true" });
    }, [dispatch]);

    const calculateTime = (start, end, log) => {
        if (!start) return { hours: 0, text: '-' };
        let startTimeObj = new Date(start);
        let endTimeObj = end ? new Date(end) : new Date();

        const wt = log?.working_time || { start_time: "09:00:00", end_time: "18:00:00" };
        const [sH, sM, sS = 0] = wt.start_time.split(':').map(Number);
        const [eH, eM, eS = 0] = wt.end_time.split(':').map(Number);
        
        const startLimit = new Date(startTimeObj);
        startLimit.setHours(sH, sM, sS, 0);
        
        const endLimit = new Date(startTimeObj);
        endLimit.setHours(eH, eM, eS, 0);

        if (startTimeObj < startLimit) {
            startTimeObj = startLimit;
        }
        if (endTimeObj > endLimit) {
            endTimeObj = endLimit;
        }

        const diffMs = endTimeObj - startTimeObj;
        if (diffMs <= 0 || isNaN(diffMs)) return { hours: 0, text: '0h 0m' };
        
        const totalMinutes = Math.floor(diffMs / (1000 * 60));
        const hours = Math.floor(totalMinutes / 60);
        const mins = totalMinutes % 60;
        
        const statusText = !end ? ` (Active)` : '';
        return { hours: diffMs / (1000 * 60 * 60), text: `${hours}h ${mins}m${statusText}` };
    };

    const getDayStatus = (hours) => {
        if (hours === 0) return <span style={{ color: 'gray' }}>-</span>;
        if (hours > 9) return <span style={{ color: '#9c27b0', fontWeight: 'bold' }}>Overtime</span>;
        if (hours >= 8) return <span style={{ color: '#2e7d32', fontWeight: 'bold' }}>Full Day</span>;
        return <span style={{ color: '#ed6c02', fontWeight: 'bold' }}>Short / Half Day</span>;
    };

    // Calculate quick metrics for the logged-in user
    const stats = useMemo(() => {
        let present = 0;
        let late = 0;
        let halfDay = 0;
        let leave = 0;

        logs.forEach(l => {
            const st = (l.status || '').toLowerCase();
            if (st === 'present') present++;
            else if (st === 'late') late++;
            else if (st.includes('half')) halfDay++;
            else if (st.includes('leave') || st === 'absent') leave++;
            else present++;
        });

        return {
            total: logs.length,
            present,
            late,
            halfDay,
            leave
        };
    }, [logs]);

    const tableHeadings = [
        "Date",
        "Punch In",
        "Punch Out",
        "Available Working Time",
        "Day Status",
        "Log Status"
    ];

    const tableRows = logs.map(log => {
        const timeData = calculateTime(log.punch_in_time, log.punch_out_time, log);
        const row = {};
        row.date = log.date || '-';
        row.punch_in = log.punch_in_time ? new Date(log.punch_in_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '-';
        row.punch_out = log.punch_out_time ? new Date(log.punch_out_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : (log.punch_in_time ? <span style={{ color: '#0288d1', fontWeight: 600 }}>Active</span> : '-');
        row.total_time = <span style={{ fontWeight: 'bold', color: timeData.hours > 9 ? '#9c27b0' : (!log.punch_out_time ? '#0288d1' : 'inherit') }}>{timeData.text}</span>;
        row.day_status = getDayStatus(timeData.hours);
        row.status = log.status || '-';
        return row;
    });

    return (
        <Container maxWidth="false" className="py-3">
            <ActionBar
                showGrid={[2]}
                btnImport=""
                label="My Attendance"
                setOpen={() => {}}
                hasPermission={false}
                btnLabel=""
            />

            {/* Quick Stat Cards */}
            <Grid container spacing={2} sx={{ mt: 1, mb: 3 }}>
                <Grid size={{ xs: 6, sm: 3, md: 3 }}>
                    <Paper sx={{ p: 2, borderRadius: '12px', border: '1px solid #e2e8f0', bgcolor: '#ffffff' }}>
                        <Typography fontSize={12} color="textSecondary" fontWeight={600} textTransform="uppercase">
                            Total Records
                        </Typography>
                        <Typography fontSize={24} fontWeight={800} color="#1e293b" sx={{ mt: 0.5 }}>
                            {stats.total}
                        </Typography>
                    </Paper>
                </Grid>
                <Grid size={{ xs: 6, sm: 3, md: 3 }}>
                    <Paper sx={{ p: 2, borderRadius: '12px', border: '1px solid #bbf7d0', bgcolor: '#f0fdf4' }}>
                        <Typography fontSize={12} color="#166534" fontWeight={600} textTransform="uppercase">
                            Present
                        </Typography>
                        <Typography fontSize={24} fontWeight={800} color="#15803d" sx={{ mt: 0.5 }}>
                            {stats.present}
                        </Typography>
                    </Paper>
                </Grid>
                <Grid size={{ xs: 6, sm: 3, md: 3 }}>
                    <Paper sx={{ p: 2, borderRadius: '12px', border: '1px solid #fed7aa', bgcolor: '#fff7ed' }}>
                        <Typography fontSize={12} color="#9a3412" fontWeight={600} textTransform="uppercase">
                            Late / Short
                        </Typography>
                        <Typography fontSize={24} fontWeight={800} color="#c2410c" sx={{ mt: 0.5 }}>
                            {stats.late + stats.halfDay}
                        </Typography>
                    </Paper>
                </Grid>
                <Grid size={{ xs: 6, sm: 3, md: 3 }}>
                    <Paper sx={{ p: 2, borderRadius: '12px', border: '1px solid #fecdd3', bgcolor: '#fff1f2' }}>
                        <Typography fontSize={12} color="#9f1239" fontWeight={600} textTransform="uppercase">
                            Leaves / Absent
                        </Typography>
                        <Typography fontSize={24} fontWeight={800} color="#be123c" sx={{ mt: 0.5 }}>
                            {stats.leave}
                        </Typography>
                    </Paper>
                </Grid>
            </Grid>

            <Paper elevation={0} sx={{ mt: 2, borderRadius: '12px', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                <XTable
                    head={tableHeadings}
                    rows={tableRows}
                />
            </Paper>
        </Container>
    );
}
